import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { controlToastState, Footer, openModal } from "risa-oasis-ui_v2";
import { updateMedicalPaOrder } from "../../api/firebase/firestoreService";
import { getNccnGuideLines } from "../../api/postCall/nccnGuidelines";
import BadgeWithIconRounded from "../../components/badgeWithIconRounded/badgeWithIconRounded";
import FormSections from "../../components/formSections/formSections";
import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import { usePharmaFormFields } from "../../context/pharmaFormFieldsContext";
import { HpcsCode } from "../../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../../enums/eventsData";
import { EventsName } from "../../enums/eventsName";
import { ModalId } from "../../enums/modalId";
import { useHealthCareFacility } from "../../hooks/useHealthCareFacility";
import { fetchMedicalPaClinicalData } from "../../redux/slice/medicalPa/medicalPaClinicalDataSlice";
import { getMedicalPaOrderDetailsFor } from "../../redux/slice/medicalPa/singleMedicalPaOrder";
import { AppDispatch, RootState } from "../../redux/store/store";
import { generateFullName } from "../../utils/generateOrderDataForTable";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { apiWorkerManager } from "../../workerManagers/workerManager";
import { getBadgeDetailsAndIconColorForCareTag } from "../singleOrderLayout/components/busniessOffice/utils/getBadgeAndIconColorForCareTag";
import MedicalPaForm from "./components/medicalPaForm";
import ThreeDotsWithClickableOptions from "./components/threeDotsWithClickableOptions";
import { createOrderDataFromContextFormData } from "./components/utils/createOrderDataFromContextFormData";
import { mergedHpcsCodeWithUniqueJcode } from "./components/utils/mergedHpcsCodeWithUniqueJcode";

const MedicalPaFormLayout = () => {
  const { id } = useParams();
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [items, setItems] = useState<any>([]);

  const { singleMedicalPaOrder, isLoading, unsubscribe } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );
  const { openedModalId } = useSelector((state: RootState) => state.modal);
  const {
    data: singleMedicalPaFormConfig,
    loading: isSingleMedicalPaFormConfigLoading,
  } = useSelector((state: RootState) => state.singleMedicalPaFormConfig);

  const { data: workflowConfig, isLoading: isWorkflowConfigLoading } =
    useSelector((state: RootState) => state.workflowConfig);

  const {
    loading: isMedicalPaClinicalDataLoading,
    unsubscribe: medicalPaClinicalDataUnsubscribe,
  } = useSelector((state: RootState) => state.medicalPaClinicalData);
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const orgId = getOrgIdForFetchExternalWorklist();

  const { loading: medicalPaFormConfigLoading } = useSelector(
    (state: RootState) => state.medicalPaFormConfig,
  );
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [nccnLoading, setNccnLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { formFieldsData, isAllFieldsValidationFree, setFormDirty } =
    usePharmaFormFields();
  const { healthCareFacilityData } = useHealthCareFacility(
    getOrgIdForFetchExternalWorklist() ?? "",
  );

  useEffect(() => {
    if (id) {
      dispatch(getMedicalPaOrderDetailsFor(id));
      dispatch(fetchMedicalPaClinicalData(id));
    }
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [dispatch, id]);

  useEffect(() => {
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      if (medicalPaClinicalDataUnsubscribe) {
        medicalPaClinicalDataUnsubscribe();
      }
    };
  }, []);

  const getNccnGuideLinesForAnOrder = async () => {
    setNccnLoading(true);
    const item = await getNccnGuideLines({
      order_id: id ?? "",
    });

    setItems(item?.data);
    setNccnLoading(false);
  };

  useEffect(() => {
    if (id) {
      getNccnGuideLinesForAnOrder();
    }
  }, [id]);

  const updateSingleOrderIdWithFormData = async () => {
    if (isWorkflowConfigLoading) {
      return;
    }

    const data = createOrderDataFromContextFormData(
      formFieldsData,
      singleMedicalPaOrder ?? {},
    );

    const mergedUniqueHpcsCodes = mergedHpcsCodeWithUniqueJcode(
      singleMedicalPaOrder?.hpcs_codes ?? ([] as HpcsCode[]),
      data?.hpcs_codes ?? ([] as HpcsCode[]),
    );

    data.hpcs_codes = mergedUniqueHpcsCodes;
    data.selected_form_id_for_submission = singleMedicalPaFormConfig?.id ?? "";

    setLoading(true);

    try {
      await updateMedicalPaOrder(singleMedicalPaOrder?.id ?? "", data);

      apiWorkerManager
        .enqueueAndProcess({
          patient_id: singleMedicalPaOrder?.patient_id ?? "",
          order_id: singleMedicalPaOrder?.order_id ?? "",
          org_id: orgId ?? "",
          event_name: EventsName.CONTINUE_BUTTON_CLICKED,
          user_id: user?.id ?? "",
          user_email: user?.email ?? "",
          additional_data: {
            screen_name: ScreenNamesForBigQuery.FORM_SCREEN,
            source: ScreenNamesForBigQuery.FORM_SCREEN,
            auth_status: singleMedicalPaOrder?.auth_on_file?.auth_status ?? "",
            form_name: singleMedicalPaFormConfig?.data?.formTitle ?? "",
          },
        })
        .catch((error) => {
          // Failed to log event to BigQuery - silently handle error
        });
      controlToastState("form-save-success");
      setFormDirty(false);
      if (workflowConfig?.submission_type === "fax") {
        navigate(`/worklists/procedure-questionaire/${id}`);
      } else {
        navigate(`/worklists/final-document/${id}`);
      }
    } catch (error) {
      controlToastState("form-save-failure");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (openedModalId === ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION) {
      openModal(ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION);
    }
  }, [openedModalId]);

  return (
    <div className="medical-pharma-pa-form__container flex h-full flex-col bg-white">
      {isLoading ||
      medicalPaFormConfigLoading ||
      isSingleMedicalPaFormConfigLoading ||
      isMedicalPaClinicalDataLoading ||
      nccnLoading ? (
        <div className="flex h-full items-center justify-center">
          <LoaderMessage message="Fetching form details..." />
        </div>
      ) : (
        <>
          <div className="medical-pharma-pa-form__body flex flex-1 flex-col overflow-hidden rounded bg-white px-4 pb-0 pt-2">
            <div className="medical-pharma-pa-form__body--header mb-4">
              <div className="medical-pharma-pa-form__body--header-details flex items-center gap-2">
                <div className="medical-pharma-pa-form__body patient-name text-h16 font-semibold leading-6 text-primaryGray-1">
                  {generateFullName(
                    singleMedicalPaOrder?.demographics?.first_name ?? "",
                    "",
                    singleMedicalPaOrder?.demographics?.last_name ?? "",
                  )}
                </div>
                <div className="medical-pharma-pa-form__body page-name text-sm font-semiBold text-black">
                  ({singleMedicalPaOrder?.demographics?.patient_mrn ?? ""})
                </div>
                {singleMedicalPaOrder?.auth_on_file?.flowsheet_regimen_details
                  ?.regimen_tag &&
                  singleMedicalPaOrder?.auth_on_file?.flowsheet_regimen_details
                    ?.regimen_tag !== "" && (
                    <BadgeWithIconRounded
                      text={
                        getBadgeDetailsAndIconColorForCareTag(
                          singleMedicalPaOrder?.auth_on_file
                            ?.flowsheet_regimen_details?.regimen_tag ?? "",
                        ).badgeText
                      }
                      icon={
                        <div
                          style={{
                            width: "10px",
                            height: "10px",
                            background: getBadgeDetailsAndIconColorForCareTag(
                              singleMedicalPaOrder?.auth_on_file
                                ?.flowsheet_regimen_details?.regimen_tag ?? "",
                            ).iconColor,
                            borderRadius: "9999px",
                          }}
                        ></div>
                      }
                      id="id2"
                      bgColor={
                        getBadgeDetailsAndIconColorForCareTag(
                          singleMedicalPaOrder?.auth_on_file
                            ?.flowsheet_regimen_details?.regimen_tag ?? "",
                        ).badgeBgColor
                      }
                      textColor={
                        getBadgeDetailsAndIconColorForCareTag(
                          singleMedicalPaOrder?.auth_on_file
                            ?.flowsheet_regimen_details?.regimen_tag ?? "",
                        ).badgeTextColor
                      }
                      borderColor={
                        getBadgeDetailsAndIconColorForCareTag(
                          singleMedicalPaOrder?.auth_on_file
                            ?.flowsheet_regimen_details?.regimen_tag ?? "",
                        ).borderColor
                      }
                    />
                  )}

                <div className="ml-auto flex items-center justify-end gap-2">
                  <ThreeDotsWithClickableOptions
                    items={items}
                    healthCareFacilityData={healthCareFacilityData}
                  />
                </div>
              </div>
            </div>
            <div className="medical-pharma-pa-form__body content flex flex-1 overflow-hidden">
              <div className="medical-pharma-pa-form__body form-sections w-1/5">
                <FormSections
                  formConfiguration={
                    singleMedicalPaFormConfig
                      ? [singleMedicalPaFormConfig]
                      : undefined
                  }
                  matchedKey={singleMedicalPaFormConfig?.id ?? ""}
                  activeSection={
                    activeSectionId ? `section-${activeSectionId}` : undefined
                  }
                />
              </div>
              <div className="medical-pharma-pa-form__body main-form h-full w-4/5 overflow-auto overflow-x-hidden border border-primaryGray-16 p-4">
                <div className="form-name--container mb-4 flex w-full justify-between">
                  <div className="form-title flex-1 rounded bg-secondaryYellow-11 p-3 text-body font-bold">
                    {singleMedicalPaFormConfig?.data?.formTitle ?? ""}
                  </div>
                </div>

                <MedicalPaForm
                  docId={id ?? ""}
                  sectionOnClick={(value) => {
                    setActiveSectionId(value as string);
                  }}
                  scrollSectionId={(value) => {
                    setActiveSectionId(value as string);
                  }}
                  formConfiguration={
                    singleMedicalPaFormConfig?.data ?? undefined
                  }
                />
              </div>
            </div>
          </div>
          <div className="medical-pharma-pa-form__footer">
            <Footer
              cancelButtonText={"Go Back"}
              isDisabled={loading || !isAllFieldsValidationFree}
              submitButtonText={"Continue"}
              onCancelClick={() => {
                navigate(`/worklists/singleOrder/${id}`);
              }}
              onSubmitClick={updateSingleOrderIdWithFormData}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default MedicalPaFormLayout;
